// Shared Utilities - adapted from both projects
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

const MINUTE_IN_SECONDS = 60
const HOUR_IN_SECONDS = MINUTE_IN_SECONDS * 60
const DAY_IN_SECONDS = HOUR_IN_SECONDS * 24
const THIRTY_DAYS_IN_SECONDS = DAY_IN_SECONDS * 30

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string | number): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export function formatRelativeTime(date: Date | string | number): string {
  const now = new Date()
  const d = new Date(date)
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1_000)

  if (diffInSeconds < MINUTE_IN_SECONDS) return 'just now'
  if (diffInSeconds < HOUR_IN_SECONDS) {
    return `${Math.floor(diffInSeconds / MINUTE_IN_SECONDS)}m ago`
  }
  if (diffInSeconds < DAY_IN_SECONDS) {
    return `${Math.floor(diffInSeconds / HOUR_IN_SECONDS)}h ago`
  }
  if (diffInSeconds < THIRTY_DAYS_IN_SECONDS) {
    return `${Math.floor(diffInSeconds / DAY_IN_SECONDS)}d ago`
  }
  
  return formatDate(date)
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => { inThrottle = false }, limit)
    }
  }
}

export function capitalize(input: string): string {
  if (!input) return ''
  return input.charAt(0).toUpperCase() + input.slice(1)
}







